import React, { useCallback, useEffect, useState } from 'react';
import { Activity, RefreshCw, AlertTriangle, CheckCircle, Database, Play, Send } from 'lucide-react';
import {
    fetchPrsStatus,
    flushPrsOutbox,
    fetchPrsReconciliationLatest,
    runPrsReconciliation,
    runPrsDigest,
    fetchDmrUsage,
    PrsStatus,
    PrsFlushReport,
    PrsReconciliationReport,
    DmrUsage,
} from '../../lib/api';
import { formatDate } from '../../lib/utils';

/**
 * PRS Health — the admin face of R4/R7/R13 (KBH launch plan §9, §7.7).
 * Outbox depth/age + 24h sync rate, manual outbox flush (the 60-min recovery
 * button), and the latest/queued PRS↔DB reconciliation. Admin-only; read-mostly
 * with three explicit operator actions.
 */

const Stat: React.FC<{ label: string; value: React.ReactNode; tone?: 'ok' | 'warn' | 'crit' | 'muted' }> = ({ label, value, tone = 'muted' }) => {
    const toneClass = {
        ok: 'text-green-700',
        warn: 'text-amber-700',
        crit: 'text-red-700',
        muted: 'text-gray-900',
    }[tone];
    return (
        <div>
            <div className="text-gray-500 text-xs uppercase tracking-wide">{label}</div>
            <div className={`font-semibold text-lg ${toneClass}`}>{value}</div>
        </div>
    );
};

const PrsHealthPage: React.FC = () => {
    const [status, setStatus] = useState<PrsStatus | null>(null);
    const [recon, setRecon] = useState<PrsReconciliationReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [flushReport, setFlushReport] = useState<PrsFlushReport | null>(null);
    const [actionMsg, setActionMsg] = useState<string | null>(null);
    const [dmrUsage, setDmrUsage] = useState<DmrUsage | null>(null);
    const [dmrError, setDmrError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [s, r] = await Promise.all([fetchPrsStatus(), fetchPrsReconciliationLatest()]);
            setStatus(s);
            setRecon(r.latest);
        } catch (err: any) {
            setError(err?.message || 'Failed to load PRS health');
        } finally {
            setLoading(false);
        }
        // DMR usage is supplementary: a failure here must not blank the page,
        // so it loads separately and only sets its own error state.
        try {
            setDmrUsage(await fetchDmrUsage());
            setDmrError(null);
        } catch (err: any) {
            setDmrError(err?.message || 'Failed to load DMR usage');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onFlush = async () => {
        setBusy('flush');
        setActionMsg(null);
        try {
            const res = await flushPrsOutbox();
            setFlushReport(res.report);
            setActionMsg(`Flush done: ${res.report.resolved} resolved, ${res.report.pendingRemaining} still pending, ${res.report.failedTotal} failed.`);
            await load();
        } catch (err: any) {
            setActionMsg(err?.message || 'Flush failed');
        } finally {
            setBusy(null);
        }
    };

    const onRunRecon = async () => {
        setBusy('recon');
        setActionMsg(null);
        try {
            const res = await runPrsReconciliation();
            setRecon(res.report);
            setActionMsg(res.report.skipped ? `Reconciliation skipped: ${res.report.skipReason}` : (res.report.ok ? 'Reconciliation clean ✔' : 'Reconciliation found discrepancies — see below.'));
        } catch (err: any) {
            setActionMsg(err?.message || 'Reconciliation failed');
        } finally {
            setBusy(null);
        }
    };

    const onRunDigest = async () => {
        setBusy('digest');
        setActionMsg(null);
        try {
            const res = await runPrsDigest();
            setActionMsg(`Digest sent — ${res.alertsRaised} alert(s) raised.`);
            await load();
        } catch (err: any) {
            setActionMsg(err?.message || 'Digest failed');
        } finally {
            setBusy(null);
        }
    };

    const ob = status?.outbox;
    const outboxTone: 'ok' | 'warn' | 'crit' = !ob ? 'ok' : (ob.oldestPendingAgeMinutes > 15 || ob.failed > 0 ? 'crit' : (ob.pending > 0 ? 'warn' : 'ok'));

    // Budget headroom. Warn at the same 80% the backend alerts on, so the page
    // and the alert agree about what "getting close" means.
    const dmrBudgetPct = dmrUsage && dmrUsage.budget > 0
        ? Math.round((dmrUsage.total / dmrUsage.budget) * 100)
        : 0;
    const dmrTone: 'ok' | 'warn' | 'crit' = dmrBudgetPct >= 100 ? 'crit' : (dmrBudgetPct >= 80 ? 'warn' : 'ok');
    const failRate = status && status.last24h.total > 0 ? status.last24h.failure / status.last24h.total : 0;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Activity className="h-6 w-6 text-indigo-600" /> PRS Health
                    </h1>
                    <p className="text-gray-600">Copenhagen parking-rights delivery — monitoring &amp; operations (R4 / R7 / R13)</p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
                    title="Refresh"
                >
                    <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
            )}
            {actionMsg && (
                <div className="bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-lg p-3 text-sm">{actionMsg}</div>
            )}

            {/* Status + outbox */}
            <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Status</h2>
                    {status && (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {status.enabled ? `PRS enabled · ${status.environment}` : 'PRS disabled'}
                        </span>
                    )}
                </div>
                {loading && !status ? (
                    <p className="text-gray-500 text-sm">Loading…</p>
                ) : status ? (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
                        <Stat label="Outbox pending" value={ob?.pending ?? 0} tone={ob && ob.pending > 0 ? 'warn' : 'ok'} />
                        <Stat label="Oldest pending" value={`${ob?.oldestPendingAgeMinutes ?? 0} min`} tone={outboxTone} />
                        <Stat label="Failed (perm.)" value={ob?.failed ?? 0} tone={ob && ob.failed > 0 ? 'crit' : 'ok'} />
                        <Stat label="PRS calls 24h" value={status.last24h.total} />
                        <Stat label="Failures 24h" value={`${status.last24h.failure} (${Math.round(failRate * 100)}%)`} tone={failRate > 0.2 ? 'crit' : 'ok'} />
                    </div>
                ) : null}
                <div className="mt-5 flex flex-wrap gap-3">
                    <button
                        onClick={onFlush}
                        disabled={busy !== null}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        <Database className="h-4 w-4" /> {busy === 'flush' ? 'Flushing…' : 'Flush outbox now'}
                    </button>
                    <button
                        onClick={onRunDigest}
                        disabled={busy !== null}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                    >
                        <Send className="h-4 w-4" /> {busy === 'digest' ? 'Sending…' : 'Run digest'}
                    </button>
                </div>
                {flushReport && (
                    <p className="mt-3 text-xs text-gray-500">
                        Last flush: scanned {flushReport.scanned}, resolved {flushReport.resolved}, retained {flushReport.transientRetained}, deferred {flushReport.deferred}, failed {flushReport.permanentFailed} ({flushReport.durationMs} ms).
                    </p>
                )}
            </div>

            {/* Reconciliation (R13) */}
            <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">PRS ↔ DB reconciliation</h2>
                    {recon && !recon.skipped && (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${recon.ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {recon.ok ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                            {recon.ok ? 'In sync' : 'Discrepancies'}
                        </span>
                    )}
                </div>
                {recon ? (
                    <>
                        <p className="text-sm text-gray-600 mb-3">
                            Last run {formatDate(recon.ranAt)}{recon.environment ? ` · ${recon.environment}` : ''}
                            {recon.skipped ? ` · skipped (${recon.skipReason})` : ''}
                        </p>
                        {recon.counts && (
                            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-sm">
                                <Stat label="Our sessions" value={recon.counts.ourSessions} />
                                <Stat label="PRS rights" value={recon.counts.theirRights} />
                                <Stat label="Matched" value={recon.counts.matched} tone="ok" />
                                <Stat label="Missing in PRS" value={recon.counts.missingInPrs} tone={recon.counts.missingInPrs > 0 ? 'crit' : 'ok'} />
                                <Stat label="Mismatched" value={recon.counts.mismatched} tone={recon.counts.mismatched > 0 ? 'crit' : 'ok'} />
                                <Stat label="Stale/extra" value={recon.counts.staleUnsynced + recon.counts.extraInPrs} tone={(recon.counts.staleUnsynced + recon.counts.extraInPrs) > 0 ? 'warn' : 'ok'} />
                            </div>
                        )}
                        {recon.missingInPrs && recon.missingInPrs.length > 0 && (
                            <div className="mt-4 border-t pt-3">
                                <div className="text-xs font-medium text-red-700 mb-1">Synced sessions PRS has no record of (fine risk):</div>
                                <ul className="text-xs text-gray-600 space-y-0.5 max-h-40 overflow-auto">
                                    {recon.missingInPrs.map((m) => (
                                        <li key={m.sessionId} className="font-mono">{m.sessionId} {m.prid ? `(prid ${m.prid})` : ''}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-gray-500 text-sm">No reconciliation has run yet.</p>
                )}
                <div className="mt-5">
                    <button
                        onClick={onRunRecon}
                        disabled={busy !== null}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        <Play className="h-4 w-4" /> {busy === 'recon' ? 'Running…' : 'Run reconciliation now'}
                    </button>
                </div>
            </div>

            {/* DMR lookup usage — cost/abuse visibility on the public lookup endpoint */}
            <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">DMR lookup usage</h2>
                    {dmrUsage && (
                        <span className="text-xs text-gray-500">{dmrUsage.day} (Europe/Copenhagen)</span>
                    )}
                </div>
                <p className="text-xs text-gray-500 mb-4">
                    Plate verification is public so guests can verify mid-checkout. Past the daily budget,
                    lookups soft-fail instead of calling the provider — parking is never blocked.
                </p>

                {dmrError ? (
                    <p className="text-amber-700 text-sm">{dmrError}</p>
                ) : !dmrUsage ? (
                    <p className="text-gray-500 text-sm">Loading…</p>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                            <Stat label="Lookups today" value={dmrUsage.total} tone={dmrTone} />
                            <Stat label="Daily budget" value={dmrUsage.budget} />
                            <Stat
                                label="Budget used"
                                value={`${dmrBudgetPct}%`}
                                tone={dmrTone}
                            />
                            <Stat label="Distinct sources" value={dmrUsage.topSources.length} />
                        </div>

                        {dmrTone === 'crit' && (
                            <div className="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>
                                    Budget exhausted — lookups are soft-failing until Danish midnight. Customers can
                                    still park and still save plates, but plates are not being DMR-verified.
                                </span>
                            </div>
                        )}

                        {dmrUsage.topSources.length > 0 && (
                            <div className="mt-5">
                                <h3 className="text-sm font-medium text-gray-700 mb-2">Busiest sources today</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-500 text-xs uppercase tracking-wide">
                                                <th className="py-1 pr-4">Source (hashed)</th>
                                                <th className="py-1">Lookups</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dmrUsage.topSources.map((src) => (
                                                <tr key={src.ipHash} className="border-t">
                                                    <td className="py-1 pr-4 font-mono text-xs text-gray-600">
                                                        {src.ipHash.slice(0, 12)}…
                                                    </td>
                                                    <td className={`py-1 font-medium ${src.count > 40 ? 'text-red-700' : 'text-gray-900'}`}>
                                                        {src.count}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-xs text-gray-400 mt-2">
                                    Salted hashes, not IP addresses. Counts only — no plates are recorded.
                                    One source above the 40/day per-IP limit means it is being rate-limited.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PrsHealthPage;
